"use client";

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getCustomers,
  addCustomer,
  updateCustomer,
  deleteCustomer,
  reset,
} from "@/state/customers/customer.slice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DotsThreeVertical,
  MagnifyingGlass,
  Plus,
  Trash,
  PencilSimple,
  ClockCounterClockwise,
  WarningCircle,
} from "@phosphor-icons/react";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, DollarSign, Pencil, Trash2 } from "lucide-react";
import axios from "axios";
import { usePermission } from "@/hooks/usePermission";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

export default function CustomersPage() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { customers, isLoading, error, message, success } = useSelector(
    (state) => state.customer
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  // History Drawer State
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [customerPayments, setCustomerPayments] = useState([]);
  const [unpaidSales, setUnpaidSales] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Payment Dialog State
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [paymentSaleId, setPaymentSaleId] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [deletePaymentId, setDeletePaymentId] = useState(null);

  // Permissions
  const canManagePayments = usePermission('ManagePayments');

  useEffect(() => {
    dispatch(getCustomers());
    return () => dispatch(reset());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: typeof error === "string" ? error : "An error occurred",
      });
      dispatch(reset());
    }
    if (success && message) {
      toast({
        title: "Success",
        description: message,
        className: "bg-green-50 dark:bg-green-900/30 border-green-200",
      });
      dispatch(reset());
    }
  }, [error, success, message, toast, dispatch]);

  const filteredCustomers = customers?.filter(
    (customer) =>
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.includes(searchTerm)
  ) || [];

  const handleAddCustomer = async () => {
    if (!newCustomer.name) {
      toast({ title: "Verification", description: "Name is required", variant: "destructive" });
      return;
    }
    await dispatch(addCustomer(newCustomer));
    setNewCustomer({ name: "", email: "", phone: "", address: "" });
    setIsAddDialogOpen(false);
  };

  const handleEditCustomer = async () => {
    if (!editingCustomer.name) return;
    await dispatch(updateCustomer(editingCustomer));
    setEditingCustomer(null);
    setIsEditDialogOpen(false);
  };

  const handleDeleteCustomer = async () => {
    if (customerToDelete) {
      await dispatch(deleteCustomer(customerToDelete.id)); // Use .id NOT ._id
      setCustomerToDelete(null);
      setIsDeleteDialogOpen(false);
    }
  };

  const loadHistory = async (customer) => {
    setSelectedCustomer(customer);
    setIsHistoryOpen(true);
    setIsLoadingHistory(true);
    try {
      // Fetch detailed customer info including purchase history
      const res = await axios.get(`${import.meta.env.VITE_API_URL}customers/${customer.id}`, { withCredentials: true });
      if (res.data.success) {
        const purchases = res.data.data.purchases || [];
        setCustomerHistory(purchases);

        // Extract all payments from all sales
        const allPayments = [];
        const unpaid = [];
        purchases.forEach(sale => {
          if (sale.payments && sale.payments.length > 0) {
            sale.payments.forEach(p => {
              allPayments.push({ ...p, sale_order: sale.order_number, sale_total: sale.total });
            });
          }
          if (sale.payment_status !== 'paid' && sale.status !== 'returned') {
            const paidAmount = sale.payments ? sale.payments.reduce((sum, p) => sum + parseFloat(p.amount), 0) : 0;
            unpaid.push({ ...sale, outstanding: parseFloat(sale.total) - paidAmount });
          }
        });
        setCustomerPayments(allPayments.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date)));
        setUnpaidSales(unpaid);

        // Update selected customer with stats
        setSelectedCustomer({ ...customer, ...res.data.data.stats });
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to load history", variant: "destructive" });
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentSaleId || !paymentAmount) {
      toast({ variant: "destructive", title: "Error", description: "Please select a sale and enter amount" });
      return;
    }
    setIsSubmittingPayment(true);
    try {
      await axios.post(`${import.meta.env.VITE_API_URL}payments`, {
        sale_id: paymentSaleId,
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod,
      }, { withCredentials: true });

      toast({ title: "Success", description: "Payment recorded successfully" });
      setIsPaymentDialogOpen(false);
      setPaymentAmount("");
      setPaymentSaleId("");

      // Refresh customer data
      dispatch(getCustomers());
      if (selectedCustomer) {
        loadHistory(selectedCustomer);
      }
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.response?.data?.message || "Failed to record payment" });
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const openPaymentDialog = (customer) => {
    setSelectedCustomer(customer);
    setPaymentSaleId("");
    setPaymentAmount("");
    setPaymentMethod("Cash");
    // Load unpaid sales for this customer
    loadUnpaidSalesForPayment(customer.id);
    setIsPaymentDialogOpen(true);
  };

  const loadUnpaidSalesForPayment = async (customerId) => {
    try {
      const res = await axios.get(`${import.meta.env.VITE_API_URL}customers/${customerId}`, { withCredentials: true });
      if (res.data.success) {
        const purchases = res.data.data.purchases || [];
        const unpaid = purchases
          .filter(sale => sale.payment_status !== 'paid' && sale.status !== 'returned')
          .map(sale => {
            const paidAmount = sale.payments ? sale.payments.reduce((sum, p) => sum + parseFloat(p.amount), 0) : 0;
            return { ...sale, outstanding: parseFloat(sale.total) - paidAmount };
          });
        setUnpaidSales(unpaid);
      }
    } catch (err) {
      console.error("Failed to load unpaid sales:", err);
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentAmount(payment.amount.toString());
    setPaymentMethod(payment.payment_method);
    setIsPaymentDialogOpen(true);
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment || !paymentAmount) return;
    setIsSubmittingPayment(true);
    try {
      await axios.put(`${import.meta.env.VITE_API_URL}payments/${editingPayment.id}`, {
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod,
      }, { withCredentials: true });

      toast({ title: "Success", description: "Payment updated successfully" });
      setIsPaymentDialogOpen(false);
      setEditingPayment(null);
      setPaymentAmount("");

      // Refresh customer data
      dispatch(getCustomers());
      if (selectedCustomer) {
        loadHistory(selectedCustomer);
      }
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.response?.data?.message || "Failed to update payment" });
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const handleDeletePayment = async () => {
    if (!deletePaymentId) return;
    setIsSubmittingPayment(true);
    try {
      await axios.delete(`${import.meta.env.VITE_API_URL}payments/${deletePaymentId}`, { withCredentials: true });
      toast({ title: "Success", description: "Payment deleted successfully" });
      setDeletePaymentId(null);

      // Refresh customer data
      dispatch(getCustomers());
      if (selectedCustomer) {
        loadHistory(selectedCustomer);
      }
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.response?.data?.message || "Failed to delete payment" });
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  return (
    <div className="mx-auto p-4 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Customers</h1>
          <p className="text-muted-foreground">Manage customer profiles and view history</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Add New Customer</DialogTitle>
              <DialogDescription>
                Enter customer details below.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleAddCustomer} disabled={isLoading}>
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Customer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center space-x-2 bg-white dark:bg-slate-900 p-2 border w-fit">
        <MagnifyingGlass className="w-5 h-5 text-gray-500 dark:text-gray-400 dark:text-gray-500" />
        <Input
          placeholder="Search by name, email or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border-none shadow-none focus-visible:ring-0 w-[300px]"
        />
      </div>

      <Card className="enterprise-table-container">
        <div>
          {isLoading && customers.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Total Spent</TableHead>
                  <TableHead>Debt</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center h-32 text-muted-foreground">
                      No customers found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((customer) => (
                    <TableRow key={customer.id}>
                      <TableCell className="font-medium">
                        <div>{customer.name}</div>
                        <div className="text-xs text-muted-foreground">{customer.address}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{customer.email}</div>
                        <div className="text-sm text-muted-foreground">{customer.phone}</div>
                      </TableCell>
                      <TableCell>GHS {parseFloat(customer.totalSpent || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        {customer.debt > 0 ? (
                          <Badge variant="destructive">GHS {parseFloat(customer.debt).toFixed(2)}</Badge>
                        ) : (
                          <span className="text-green-600 text-sm">Clear</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <DotsThreeVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => loadHistory(customer)}>
                              <ClockCounterClockwise className="mr-2 h-4 w-4" /> History
                            </DropdownMenuItem>
                            {customer.debt > 0 && (
                              <DropdownMenuItem onClick={() => openPaymentDialog(customer)}>
                                <DollarSign className="mr-2 h-4 w-4" /> Record Payment
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => {
                              setEditingCustomer(customer);
                              setIsEditDialogOpen(true);
                            }}>
                              <PencilSimple className="mr-2 h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => {
                              setCustomerToDelete(customer);
                              setIsDeleteDialogOpen(true);
                            }} className="text-red-600">
                              <Trash className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          {editingCustomer && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editingCustomer.name}
                  onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    value={editingCustomer.email}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-phone">Phone</Label>
                  <Input
                    id="edit-phone"
                    value={editingCustomer.phone}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-address">Address</Label>
                <Input
                  id="edit-address"
                  value={editingCustomer.address}
                  onChange={(e) => setEditingCustomer({ ...editingCustomer, address: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={handleEditCustomer} disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Customer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {customerToDelete?.name}? This action cannot be undone.
              Existing sales history will prevent deletion.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteCustomer} disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Sheet */}
      <Sheet open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
        <SheetContent className="sm:max-w-[600px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Customer History</SheetTitle>
            <SheetDescription>Transaction history for {selectedCustomer?.name}</SheetDescription>
          </SheetHeader>

          {isLoadingHistory ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin" /></div>
          ) : (
            <div className="mt-6 space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">GHS {parseFloat(selectedCustomer?.totalSpent || selectedCustomer?.stats?.totalSpent || 0).toFixed(2)}</div>
                    <p className="text-xs text-muted-foreground">Total Spent</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{selectedCustomer?.salesCount || selectedCustomer?.stats?.salesCount || 0}</div>
                    <p className="text-xs text-muted-foreground">Purchases</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className={`text-2xl font-bold ${(selectedCustomer?.debt || selectedCustomer?.stats?.totalDebt || 0) > 0 ? 'text-red-500' : 'text-green-500'}`}>
                      GHS {parseFloat(selectedCustomer?.debt || selectedCustomer?.stats?.totalDebt || 0).toFixed(2)}
                    </div>
                    <p className="text-xs text-muted-foreground">Current Debt</p>
                  </CardContent>
                </Card>
              </div>

              {/* Record Payment Button */}
              {(selectedCustomer?.debt > 0 || selectedCustomer?.stats?.totalDebt > 0) && (
                <Button onClick={() => openPaymentDialog(selectedCustomer)} className="w-full">
                  <DollarSign className="mr-2 h-4 w-4" /> Record Payment
                </Button>
              )}

              <Tabs defaultValue="transactions" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="transactions">Transactions</TabsTrigger>
                  <TabsTrigger value="payments">Payments</TabsTrigger>
                </TabsList>

                <TabsContent value="transactions" className="mt-4">
                  <div className="space-y-4">
                    {customerHistory.length === 0 ? (
                      <p className="text-muted-foreground text-sm text-center py-4">No transactions found.</p>
                    ) : (
                      customerHistory.map((sale) => (
                        <div key={sale.id} className="flex justify-between items-center border-b pb-4 last:border-0">
                          <div>
                            <div className="font-medium text-sm">Order #{sale.order_number || sale.id}</div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(sale.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-sm">GHS {parseFloat(sale.total).toFixed(2)}</div>
                            <Badge variant={sale.payment_status === 'paid' ? 'success' : sale.payment_status === 'partial' ? 'warning' : 'destructive'} className="text-[10px] h-5">
                              {sale.payment_status?.toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="payments" className="mt-4">
                  <div className="space-y-4">
                    {customerPayments.length === 0 ? (
                      <p className="text-muted-foreground text-sm text-center py-4">No payments recorded.</p>
                    ) : (
                      customerPayments.map((payment, idx) => (
                        <div key={idx} className="flex justify-between items-center border-b pb-4 last:border-0">
                          <div>
                            <div className="font-medium text-sm text-green-600">+ GHS {parseFloat(payment.amount).toFixed(2)}</div>
                            <div className="text-xs text-muted-foreground">
                              {payment.payment_method} • {new Date(payment.payment_date).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-muted-foreground">For: {payment.sale_order}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px]">
                              {payment.payment_method}
                            </Badge>
                            {canManagePayments && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => handleEditPayment(payment)}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                                  onClick={() => setDeletePaymentId(payment.id)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={(open) => {
        setIsPaymentDialogOpen(open);
        if (!open) {
          setEditingPayment(null);
          setPaymentAmount("");
          setPaymentMethod("Cash");
          setPaymentSaleId("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPayment ? "Edit Payment" : "Record Payment"}</DialogTitle>
            <DialogDescription>
              {editingPayment
                ? `Editing payment of GHS ${parseFloat(editingPayment.amount).toFixed(2)}`
                : "Select a sale and enter the payment amount."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {!editingPayment && (
              <div className="grid gap-2">
                <Label>Select Sale</Label>
                <Select value={paymentSaleId} onValueChange={setPaymentSaleId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select unpaid sale..." />
                  </SelectTrigger>
                  <SelectContent>
                    {unpaidSales.map(sale => (
                      <SelectItem key={sale.id} value={sale.id.toString()}>
                        #{sale.order_number} - Outstanding: GHS {sale.outstanding?.toFixed(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid gap-2">
              <Label>Amount (GHS)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount..."
              />
            </div>
            <div className="grid gap-2">
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Momo">Mobile Money</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="Card">Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPaymentDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={editingPayment ? handleUpdatePayment : handleRecordPayment}
              disabled={isSubmittingPayment || (!editingPayment && !paymentSaleId) || !paymentAmount}
            >
              {isSubmittingPayment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <DollarSign className="h-4 w-4 mr-2" />}
              {editingPayment ? "Update Payment" : "Record Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Payment Confirmation Dialog */}
      <Dialog open={!!deletePaymentId} onOpenChange={(open) => !open && setDeletePaymentId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Payment</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this payment? This action cannot be undone and will restore the outstanding balance.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePaymentId(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDeletePayment}
              disabled={isSubmittingPayment}
            >
              {isSubmittingPayment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
